// src/components/banking/OtherInPanel.tsx — "What is it?" for money in that is not rent (prototype §42 BANK_OTHER_IN;
// Task T): a tenancy deposit (marks it received, Dr Bank / Cr Tenant deposits held), service charge, ground rent,
// insurance recharge, bank interest or other income — each booked straight to its ledger account from the line.
import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { api } from '../../lib/api';
import { bankingApi, type BankLine } from '../../lib/banking';
import { Button, Field, Input } from '../ui';
import { FormError } from '../forms/common';

export type WhatIsIt = 'RENT' | 'DEPOSIT' | 'SERVICE_CHARGE' | 'GROUND_RENT' | 'INSURANCE_RECHARGE' | 'INTEREST' | 'OTHER';
export const WHAT_IS_IT: Array<[WhatIsIt, string]> = [
  ['RENT', 'Rent'], ['DEPOSIT', 'Tenancy deposit'], ['SERVICE_CHARGE', 'Service charge'], ['GROUND_RENT', 'Ground rent'],
  ['INSURANCE_RECHARGE', 'Insurance recharge'], ['INTEREST', 'Bank interest'], ['OTHER', 'Other income'],
];
const ACCOUNT: Record<Exclude<WhatIsIt, 'RENT'>, string> = {
  DEPOSIT: 'Tenant deposits held (a liability — the tenant\'s money)', SERVICE_CHARGE: 'Service charge income', GROUND_RENT: 'Ground rent income',
  INSURANCE_RECHARGE: 'Fees & recharges', INTEREST: 'Bank interest received', OTHER: 'Fees & recharges',
};

export function WhatIsItPicker({ value, onChange }: { value: WhatIsIt; onChange: (v: WhatIsIt) => void }) {
  return (
    <div className="mb-4">
      <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-ink-500">What is it?</div>
      <div className="flex flex-wrap gap-1.5" role="radiogroup">
        {WHAT_IS_IT.map(([k, l]) => (
          <button key={k} type="button" role="radio" aria-checked={value === k} onClick={() => onChange(k)}
            className={`h-7 rounded-lg border px-2.5 text-[12px] transition ${value === k ? 'border-bronze-500 bg-bronze-50 text-bronze-700' : 'border-ink-200 bg-white text-ink-600 hover:bg-ink-50'}`}>{l}</button>
        ))}
      </div>
    </div>
  );
}

export function OtherInPanel({ line, what, onDone }: { line: BankLine; what: Exclude<WhatIsIt, 'RENT'>; onDone: (msg: string) => void }) {
  const [search, setSearch] = useState(line.suggestedTenancy?.payReference ?? '');
  const [q, setQ] = useState(search);
  const [tenancy, setTenancy] = useState<{ id: string; label: string } | null>(line.suggestedTenancy ? { id: line.suggestedTenancy.id, label: `${line.suggestedTenancy.tenants ?? ''} · ${line.suggestedTenancy.unitReference}, ${line.suggestedTenancy.propertyName}` } : null);
  const [note, setNote] = useState('');
  useEffect(() => { const t = setTimeout(() => setQ(search), 250); return () => clearTimeout(t); }, [search]);
  const found = useQuery({ queryKey: ['/banking/tenancies', line.bankAccountId, q], queryFn: () => bankingApi.tenancies(line.bankAccountId, q), enabled: q.length >= 2 && !tenancy });
  const run = useMutation({
    mutationFn: async () => (await api.post<BankLine>(`/banking/lines/${line.id}/allocate-other`, { category: what, tenancyId: tenancy?.id ?? null, note: note || null })).data,
    onSuccess: (l) => onDone(`Booked as ${WHAT_IS_IT.find(([k]) => k === what)?.[1].toLowerCase()}${l.allocation?.kind === 'OTHER' && l.allocation.journalReference ? ` — journal ${l.allocation.journalReference}` : ''}.${what === 'DEPOSIT' ? ' The deposit is marked received and its 30-day clock runs from the booked date.' : ''}`),
  });
  const needsTenancy = what === 'DEPOSIT';
  return (
    <div className="space-y-3">
      <p className="text-[12.5px] text-ink-600">Books the money straight to <b>{ACCOUNT[what]}</b>{needsTenancy ? ', against the tenancy below. The payment has to be exactly the deposit recorded on the tenancy.' : '. Choose a tenancy to tag the building on the journal (optional).'}</p>
      {tenancy ? (
        <div className="flex items-center gap-2 rounded-lg border border-ink-200 px-3 py-2 text-[12.5px]"><span className="min-w-0 flex-1 truncate">{tenancy.label}</span><Button size="sm" variant="ghost" onClick={() => setTenancy(null)}>Change</Button></div>
      ) : (
        <>
          <div className="relative"><Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400" /><Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Tenant name, flat, pay reference…" className="w-full pl-9" /></div>
          {q.length >= 2 && (
            <div className="max-h-52 divide-y divide-ink-100 overflow-y-auto rounded-lg border border-ink-200">
              {found.isLoading ? <div className="p-3 text-[12px] text-ink-400">Searching…</div> : !found.data?.length ? <div className="p-3 text-[12px] text-ink-400">No tenancy in this company matches.</div>
                : found.data.map((t) => (
                  <button key={t.id} type="button" onClick={() => setTenancy({ id: t.id, label: `${t.tenants ?? ''} · ${t.unitReference}, ${t.propertyName}` })} className="block w-full px-3 py-2 text-left hover:bg-ink-50">
                    <div className="truncate text-[13px] font-medium text-ink-900">{t.tenants} · {t.unitReference}, {t.propertyName}</div><div className="truncate text-[11.5px] text-ink-500">{t.payReference}</div>
                  </button>))}
            </div>
          )}
        </>
      )}
      <Field label="Note (optional)"><Input value={note} onChange={(e) => setNote(e.target.value)} className="w-full" /></Field>
      <FormError error={run.error} />
      <div className="flex justify-end"><Button onClick={() => run.mutate()} disabled={run.isPending || (needsTenancy && !tenancy)}>{run.isPending ? 'Booking…' : `Book as ${WHAT_IS_IT.find(([k]) => k === what)?.[1].toLowerCase()}`}</Button></div>
    </div>
  );
}
